import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle2, ShieldCheck, Network, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader } from '../components/Shared';

const OFFICES = [
  { value: 'ward', label: 'Ward Councillor candidate' },
  { value: 'citywide', label: 'Citywide / team' },
  { value: 'mayor', label: 'Mayoral candidate' },
];

const USAGE_OPTIONS = [
  { value: 'direct_link', label: 'Direct hosted link for my ward' },
  { value: 'embedded_widget', label: 'Embedded widget on my website' },
  { value: 'ward_tracker', label: 'A full ward tracker for my residents' },
  { value: 'report_access', label: 'Access to privacy-safe reports' },
  { value: 'not_sure', label: 'Not sure yet (tell me more)' },
];

const EMPTY = {
  name: '', email: '', phone: '', office: 'ward', ward: '',
  website_url: '', facebook_url: '', message: '', usage: [], consent: false, website: '',
};

export default function JoinNetwork() {
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleUsage = (value) => {
    setForm((f) => ({
      ...f,
      usage: f.usage.includes(value) ? f.usage.filter((u) => u !== value) : [...f.usage, value],
    }));
  };

  const emailOk = /.+@.+\..+/.test(form.email);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Please enter your name'); return; }
    if (!emailOk) { toast.error('Please enter a valid email address'); return; }
    if (!form.consent) { toast.error('Please confirm the consent checkbox'); return; }
    setBusy(true);
    try {
      await api.post('/network/join', form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="join-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
        <h1 className="font-serif font-black text-[#1a2744] text-2xl sm:text-3xl mt-4">Request received</h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Thanks for your interest in the PITA Network. The campaign reviews every request personally.
          Nothing is listed publicly, and no tools go live, until you and the campaign both approve it.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild className="h-12 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press justify-center" data-testid="join-success-network-btn">
            <Link to="/tracker-network">Back to the Network</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-[#1a2744] text-[#1a2744] font-bold btn-press justify-center" data-testid="join-success-tracker-btn">
            <Link to="/issue-tracker">See the Ward 8 Tracker</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="join-network-page">
      <PageHeader
        kicker="PUBLIC ISSUE TRACKER APP (PITA) NETWORK"
        title="Bring PITA to Your Ward"
        subtitle="Candidates who want better tools for residents can request access to the PITA Network."
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="rounded-2xl border border-[#C9A84C]/40 bg-[#C9A84C]/10 p-4 flex gap-3 mb-6" data-testid="join-approval-note">
          <ShieldCheck className="h-5 w-5 text-[#7A5C12] shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Approval-based and opt-in only.</strong> Sending this request does not list you publicly.
            Every listing, hosted tracker, and branded material goes live only after you opt in and the campaign approves it.
          </p>
        </div>

        <Card>
          <CardContent className="p-5 sm:p-7 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Your name *</label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="h-11" data-testid="join-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Email *</label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="h-11" data-testid="join-email-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Phone (optional)</label>
                <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} className="h-11" data-testid="join-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Office sought</label>
                <Select value={form.office} onValueChange={(v) => set('office', v)}>
                  <SelectTrigger className="h-11" data-testid="join-office-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{OFFICES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.office === 'ward' && (
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Ward number</label>
                  <Input value={form.ward} onChange={(e) => set('ward', e.target.value)} placeholder="e.g. 3" className="h-11" data-testid="join-ward-input" />
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Campaign website (optional)</label>
                <Input value={form.website_url} onChange={(e) => set('website_url', e.target.value)} placeholder="https://" className="h-11" data-testid="join-website-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Facebook page (optional)</label>
                <Input value={form.facebook_url} onChange={(e) => set('facebook_url', e.target.value)} placeholder="https://facebook.com/…" className="h-11" data-testid="join-facebook-input" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-600 mb-2">How would you like to use it?</label>
              <div className="space-y-2" data-testid="join-usage-options">
                {USAGE_OPTIONS.map((u) => (
                  <label key={u.value} className="flex items-center gap-2.5 text-sm text-slate-700 font-medium cursor-pointer">
                    <Checkbox checked={form.usage.includes(u.value)} onCheckedChange={() => toggleUsage(u.value)} data-testid={`join-usage-${u.value}`} />
                    {u.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Message (optional)</label>
              <Textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4} placeholder="Tell us about your campaign and what you're looking for…" data-testid="join-message-input" />
            </div>

            {/* Honeypot — hidden from real users */}
            <input type="text" value={form.website} onChange={(e) => set('website', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer border-t pt-4" data-testid="join-consent-label">
              <Checkbox checked={form.consent} onCheckedChange={(v) => set('consent', !!v)} className="mt-0.5" data-testid="join-consent-checkbox" />
              <span>
                I understand this is a request only. I will not be listed publicly, and no tools will go live under my name,
                unless I opt in and the campaign approves it. The campaign may contact me about this request.
              </span>
            </label>

            <Button onClick={submit} disabled={busy} className="w-full h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="join-submit-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-1.5" /> Send Request</>}
            </Button>
          </CardContent>
        </Card>

        <div className="mt-8 rounded-2xl bg-[#1a2744] p-6 sm:p-8" data-testid="join-why-block">
          <Network className="h-6 w-6 text-[#C9A84C]" />
          <h2 className="text-white font-serif font-black text-xl mt-3">Why join?</h2>
          <ul className="mt-3 space-y-2 text-slate-300 text-sm leading-relaxed">
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C9A84C] shrink-0 mt-0.5" /> Give residents a real way to report issues, get a tracking number, and follow what happens next.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C9A84C] shrink-0 mt-0.5" /> Privacy-safe by design: names, contact details, and exact addresses are never shown publicly.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C9A84C] shrink-0 mt-0.5" /> Your ward's private issue data stays yours, with strict separation between campaigns.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C9A84C] shrink-0 mt-0.5" /> Hosted link or copy-paste widget, no website rebuild needed.</li>
          </ul>
          <Button asChild variant="outline" className="mt-5 h-11 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="join-learn-more-btn">
            <Link to="/tracker-network">Learn more about the Network</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
